// Message Content Component - Handles different message types

import React from 'react';
import { Activity, Info, TrendingUp, AlertTriangle, BarChart3, Lightbulb, CheckCircle2, Sparkles, Wrench } from 'lucide-react';
import { COLORS, CHART_COLORS } from '../../constants/theme';
import AreaChart from '../charts/AreaChart';
import BarChart from '../charts/BarChart';
import PieChart from '../charts/PieChart';
import ProductTable from '../common/ProductTable';
import Suggestions from './Suggestions';
import FollowupSuggestions from './FollowupSuggestions';
import KPICard from '../common/KPICard';
import { isValidUrl, extractUrl } from '../../utils/urlUtils';

// Always-expanded insight item for better readability
const InsightItem = ({ insight, getInsightIcon, getInsightColor, getTypeLabel, isRecommendation = false }) => {
  return (
    <div
      className={`
        rounded-lg border transition-all duration-300 overflow-hidden shadow-sm
        ${isRecommendation 
          ? 'bg-green-50 border-green-200 border-2' 
          : getInsightColor(insight.type)
        }
      `}
    >
      <div className="transition-all duration-300">
        <div className="flex items-start gap-3 p-3 sm:p-4 transition-colors duration-200">
          {/* Icon */}
          <div className="flex-shrink-0">
            {getInsightIcon(insight.type)}
          </div>

          {/* Type Badge */}
          <div className="flex-shrink-0">
            <span className={`
              text-xs font-medium px-2 py-1 rounded border
              ${isRecommendation
                ? 'text-green-800 bg-green-100 border-green-200'
                : 'text-gray-600 bg-white border-gray-200'
              }
            `}>
              {getTypeLabel(insight.type)}
            </span>
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className={`
              text-sm leading-relaxed
              ${isRecommendation ? 'text-gray-900 font-medium' : 'text-gray-800'}
            `}>
              {insight.text}
            </p>
          </div>

          {/* Confidence Badge */}
          {typeof insight.confidence === 'number' && (
            <div className="flex-shrink-0 hidden sm:block">
              <span className="text-xs text-gray-500 bg-white/80 px-2 py-1 rounded border border-gray-200">
                {Math.round(insight.confidence * 100)}%
              </span>
            </div>
          )}

        </div>
        {typeof insight.confidence === 'number' && (
          <div className="px-3 sm:px-4 pb-3 sm:hidden">
            <span className="text-xs text-gray-500 bg-white/80 px-2 py-1 rounded border border-gray-200">
              Confidence: {Math.round(insight.confidence * 100)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to render insights in a user-friendly way
const renderInsights = (insights) => {
  if (!insights || !Array.isArray(insights) || insights.length === 0) {
    return null;
  }

  const validInsights = insights.filter(
    insight => insight && typeof insight === 'object' && insight.text
  );

  if (validInsights.length === 0) {
    return null;
  }

  const getInsightIcon = (type) => {
    switch (type) {
      case 'trend':
        return <TrendingUp className="w-4 h-4" style={{ color: '#4F46E5' }} />;
      case 'outlier':
        return <AlertTriangle className="w-4 h-4" style={{ color: '#F59E0B' }} />;
      case 'comparison':
        return <BarChart3 className="w-4 h-4" style={{ color: '#3B82F6' }} />;
      case 'recommendation':
        return <Lightbulb className="w-4 h-4" style={{ color: '#10B981' }} />;
      case 'improvement':
        return <Wrench className="w-4 h-4" style={{ color: '#0EA5E9' }} />;
      case 'summary':
      default:
        return <Info className="w-4 h-4" style={{ color: COLORS.primary }} />;
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'trend':
        return 'bg-purple-50 border-purple-200';
      case 'outlier':
        return 'bg-yellow-50 border-yellow-200';
      case 'comparison':
        return 'bg-blue-50 border-blue-200';
      case 'recommendation':
        return 'bg-green-50 border-green-200';
      case 'improvement':
        return 'bg-cyan-50 border-cyan-200';
      case 'summary':
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      trend: 'Trend',
      outlier: 'Outlier',
      comparison: 'Comparison',
      summary: 'Summary',
      recommendation: 'Recommendation',
      improvement: 'Improvement',
    };
    return labels[type] || 'Insight';
  };

  // Separate recommendations from other insights
  const recommendations = validInsights.filter(i => i.type === 'recommendation');
  const improvements = validInsights.filter(i => i.type === 'improvement');
  const otherInsights = validInsights.filter(i => i.type !== 'recommendation' && i.type !== 'improvement');

  return (
    <div className="mt-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="p-1.5 bg-blue-50 rounded-lg">
          <Sparkles className="w-5 h-5 animate-float" style={{ color: COLORS.primary }} />
        </div>
        <h3 className="text-base font-semibold text-gray-900">
          Insights & Analysis
        </h3>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full font-medium">
          {validInsights.length}
        </span>
      </div>

      {/* Table-like Header for Desktop */}
      {otherInsights.length > 0 && (
        <div className="hidden sm:grid sm:grid-cols-12 gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 mb-2">
          <div className="col-span-1 text-xs font-semibold text-gray-600">Type</div>
          <div className="col-span-8 text-xs font-semibold text-gray-600">Insight</div>
          <div className="col-span-2 text-xs font-semibold text-gray-600 text-center">Confidence</div>
          <div className="col-span-1 text-xs font-semibold text-gray-600 text-center"></div>
        </div>
      )}

      {/* Regular Insights */}
      {otherInsights.length > 0 && (
        <div className="space-y-2">
          {otherInsights.map((insight, idx) => (
            <InsightItem
              key={idx}
              insight={insight}
              getInsightIcon={getInsightIcon}
              getInsightColor={getInsightColor}
              getTypeLabel={getTypeLabel}
              isRecommendation={false}
            />
          ))}
        </div>
      )}

      {/* Recommendations (Highlighted) */}
      {recommendations.length > 0 && (
        <div className="space-y-3 mt-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5" style={{ color: '#10B981' }} />
            <h4 className="text-sm font-semibold text-gray-900">Actionable Recommendations</h4>
            <CheckCircle2 className="w-4 h-4 text-green-500" />
          </div>
          <div className="space-y-2">
            {recommendations.map((insight, idx) => (
              <InsightItem
                key={idx}
                insight={insight}
                getInsightIcon={getInsightIcon}
                getInsightColor={getInsightColor}
                getTypeLabel={getTypeLabel}
                isRecommendation={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Improvements */}
      {improvements.length > 0 && (
        <div className="space-y-3 mt-4">
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-cyan-600" />
            <h4 className="text-sm font-semibold text-gray-900">Improvements</h4>
          </div>
          <div className="space-y-2">
            {improvements.map((insight, idx) => (
              <InsightItem
                key={idx}
                insight={insight}
                getInsightIcon={getInsightIcon}
                getInsightColor={getInsightColor}
                getTypeLabel={getTypeLabel}
                isRecommendation={false}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to transform API results to chart data
const transformToChartData = (results, metadata) => {
  if (!results || !Array.isArray(results) || results.length === 0) {
    return [];
  }

  // If results are already in chart format (array of objects), return as is
  if (results[0] && typeof results[0] === 'object' && !Array.isArray(results[0])) {
    // Ensure all rows are valid objects
    return results
      .filter(row => row !== null && row !== undefined && typeof row === 'object')
      .map((row, idx) => {
        // Clean up the row - ensure all values are properly formatted
        const cleaned = {};
        Object.keys(row).forEach(key => {
          const value = row[key];
          if (value === null || value === undefined) {
            cleaned[key] = null;
          } else if (typeof value === 'number') {
            cleaned[key] = value;
          } else if (typeof value === 'string') {
            // Try to parse as number if it looks numeric
            const num = Number(value);
            cleaned[key] = !isNaN(num) && value.trim() !== '' ? num : value;
          } else {
            cleaned[key] = value;
          }
        });
        return cleaned;
      });
  }

  // If results are simple values, wrap them
  return results.map((row, idx) => {
    if (typeof row === 'object' && row !== null) {
      return row;
    }
    // Fallback for simple values
    return { 
      value: row, 
      index: idx,
      name: `Item ${idx + 1}`,
    };
  });
};

// Helper function to transform results to KPI cards
const transformToKPICards = (results, metadata) => {
  if (!results || !Array.isArray(results) || results.length === 0) {
    return [];
  }

  // Ensure all results are valid objects
  const validResults = results.filter(r => r !== null && r !== undefined && typeof r === 'object');
  if (validResults.length === 0) {
    return [];
  }

  const firstRow = validResults[0];
  const keys = Object.keys(firstRow);

  if (keys.length === 0) {
    return [];
  }

  // Special handling: If single record, show ALL fields as cards for complete details
  if (validResults.length === 1) {
    const row = validResults[0];
    const colorPalette = CHART_COLORS;
    
    return keys.map((key, idx) => {
      const value = row[key];
      let displayValue = 'N/A';
      let isNumeric = false;
      let isUrl = false;
      
      if (value !== null && value !== undefined) {
        // Check if column name suggests it's a URL column
        const urlColumnNames = ['url', 'link', 'href', 'uri', 'file', 'attachment', 'document'];
        const isUrlColumn = urlColumnNames.some(name => key.toLowerCase().includes(name));
        
        // Check if value is a URL
        const extractedUrl = isValidUrl(value) ? value : (typeof value === 'string' ? extractUrl(value) : null);
        
        if (extractedUrl || (isUrlColumn && typeof value === 'string' && value.trim().length > 0)) {
          // If it's a URL column or contains a valid URL, preserve the URL value
          displayValue = extractedUrl || value;
          isUrl = true;
        } else if (typeof value === 'number') {
          displayValue = value.toLocaleString();
          isNumeric = true;
        } else if (typeof value === 'boolean') {
          displayValue = value ? 'Yes' : 'No';
        } else {
          const str = String(value);
          // Try to parse as number
          const num = Number(str);
          if (!isNaN(num) && isFinite(num) && str.trim() !== '' && str === String(num)) {
            displayValue = num.toLocaleString();
            isNumeric = true;
          } else {
            // Keep full value, truncation will happen in display
            displayValue = str;
          }
        }
      }
      
      return {
        title: key,
        value: displayValue,
        change: isNumeric ? 'Value' : (isUrl ? 'Link' : 'Detail'),
        trend: 'up',
        description: `${key} information`,
        color: colorPalette[idx % colorPalette.length],
        lightBg: idx % 2 === 0 ? 'bg-blue-50' : 'bg-purple-50',
        icon: Activity,
      };
    });
  }

  // Detect numeric columns dynamically for multiple rows
  const numericKeys = keys.filter(key => {
    const value = firstRow[key];
    if (value === null || value === undefined) return false;
    return typeof value === 'number' || 
           (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '');
  });

  // If we have metadata with column info, use it
  if (metadata && metadata.columns && Array.isArray(metadata.columns)) {
    const metadataNumericCols = metadata.columns.filter(col => col.type === 'number');
    if (metadataNumericCols.length > 0) {
      if (validResults.length === 1) {
        // Single row - create one card per numeric column
        const row = validResults[0];
        return metadataNumericCols.map((col, idx) => {
          const value = row[col.name];
          const numValue = typeof value === 'number' ? value : Number(value);
          const displayValue = !isNaN(numValue) ? numValue.toLocaleString() : String(value || 'N/A');
          
          return {
            title: col.name,
            value: displayValue,
            change: '+0%',
            trend: 'up',
            description: `${col.name} metric`,
            color: COLORS.primary,
            lightBg: 'bg-blue-50',
            icon: Activity,
          };
        });
      } else {
        // Multiple rows - aggregate
        return metadataNumericCols.map((col, idx) => {
          const values = validResults
            .map(r => {
              const val = r[col.name];
              return typeof val === 'number' ? val : Number(val);
            })
            .filter(v => !isNaN(v) && isFinite(v));
          
          const sum = values.reduce((a, b) => a + b, 0);
          const avg = values.length > 0 ? sum / values.length : 0;
          const count = values.length;
          
          return {
            title: col.name,
            value: avg.toLocaleString(),
            change: `${count} items`,
            trend: 'up',
            description: `Average ${col.name}`,
            color: COLORS.primary,
            lightBg: 'bg-blue-50',
            icon: Activity,
          };
        });
      }
    }
  }

  // Fallback: use detected numeric keys
  if (numericKeys.length > 0) {
    if (validResults.length === 1) {
      // Single row
      const row = validResults[0];
      return numericKeys.map((key, idx) => {
        const value = row[key];
        const numValue = typeof value === 'number' ? value : Number(value);
        const displayValue = !isNaN(numValue) ? numValue.toLocaleString() : String(value || 'N/A');
        
        return {
          title: key,
          value: displayValue,
          change: '+0%',
          trend: 'up',
          description: `${key} metric`,
          color: COLORS.primary,
          lightBg: 'bg-blue-50',
          icon: Activity,
        };
      });
    } else {
      // Multiple rows - aggregate
      return numericKeys.map((key, idx) => {
        const values = validResults
          .map(r => {
            const val = r[key];
            return typeof val === 'number' ? val : Number(val);
          })
          .filter(v => !isNaN(v) && isFinite(v));
        
        const sum = values.reduce((a, b) => a + b, 0);
        const avg = values.length > 0 ? sum / values.length : 0;
        const count = values.length;
        
        return {
          title: key,
          value: avg.toLocaleString(),
          change: `${count} items`,
          trend: 'up',
          description: `Average ${key}`,
          color: COLORS.primary,
          lightBg: 'bg-blue-50',
          icon: Activity,
        };
      });
    }
  }

  // Last resort: create cards from all columns
  return keys.slice(0, 5).map((key, idx) => {
    const value = firstRow[key];
    return {
      title: key,
      value: value !== null && value !== undefined ? String(value) : 'N/A',
      change: '+0%',
      trend: 'up',
      description: `${key} value`,
      color: COLORS.primary,
      lightBg: 'bg-blue-50',
      icon: Activity,
    };
  });
};

// Helper function to transform results to table format
const transformToTableData = (results) => {
  if (!results || !Array.isArray(results)) {
    return [];
  }
  // Return results as-is, ProductTable will handle dynamic columns
  return results.map((row, idx) => {
    // Ensure each row has an id for React keys
    if (row && typeof row === 'object') {
      return {
        id: row.id || idx,
        ...row,
      };
    }
    // If row is not an object, wrap it
    return {
      id: idx,
      value: row,
    };
  });
};

const MessageContent = ({ message, isUser, onSuggestionClick, isQueryLoading = false }) => {
  // Validate message
  if (!message || typeof message !== 'object') {
    return (
      <div className="text-sm text-gray-500">
        Invalid message format
      </div>
    );
  }

  if (message.type === 'text') {
    const content = message.content !== undefined && message.content !== null 
      ? String(message.content) 
      : '';
    
    return (
      <div>
        {content && (
          <p className={`text-sm leading-relaxed ${isUser ? 'text-white' : 'text-gray-700'}`}>
            {content}
          </p>
        )}
        {message.apiData?.insights && renderInsights(message.apiData.insights)}
        {message.apiData?.warnings && Array.isArray(message.apiData.warnings) && message.apiData.warnings.length > 0 && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
            <strong>Warnings:</strong> {message.apiData.warnings.filter(w => w).join(', ')}
          </div>
        )}
      </div>
    );
  }

  if (message.type === 'cards') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const metadata = apiData.metadata || {};
      const cards = transformToKPICards(results, metadata);

      if (cards.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No metric data available. Try asking for specific metrics or counts.
          </p>
        );
      }

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="w-4 h-4" style={{ color: COLORS.primary }} />
              Record Details
            </h3>
            {apiData.metadata && typeof apiData.metadata.rowCount === 'number' && (
              <span className="text-xs text-gray-500">
                {apiData.metadata.rowCount} result(s)
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cards.map((card, idx) => (
              <KPICard key={idx} card={card} />
            ))}
          </div>
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering cards:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering metric cards: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'line-chart') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const metadata = apiData.metadata || {};
      const chartData = transformToChartData(results, metadata);

      if (chartData.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No data available for line chart visualization.
          </p>
        );
      }

      return (
        <div>
          <AreaChart data={chartData} />
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering line chart:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering line chart: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'bar-chart') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const metadata = apiData.metadata || {};
      const chartData = transformToChartData(results, metadata);

      if (chartData.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No data available for bar chart visualization.
          </p>
        );
      }

      return (
        <div>
          <BarChart data={chartData} />
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering bar chart:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering bar chart: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'pie-chart' || message.type === 'donut-chart') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const metadata = apiData.metadata || {};
      const chartData = transformToChartData(results, metadata);

      if (chartData.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No data available for {message.type === 'donut-chart' ? 'donut' : 'pie'} chart visualization.
          </p>
        );
      }

      return (
        <div>
          <PieChart data={chartData} isDonut={message.type === 'donut-chart'} />
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering pie/donut chart:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering {message.type === 'donut-chart' ? 'donut' : 'pie'} chart: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'area-chart') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const metadata = apiData.metadata || {};
      const chartData = transformToChartData(results, metadata);

      if (chartData.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No data available for area chart visualization.
          </p>
        );
      }

      return (
        <div>
          <AreaChart data={chartData} isArea={true} />
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering area chart:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering area chart: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'table') {
    try {
      const apiData = message.apiData || {};
      const results = Array.isArray(apiData.results) ? apiData.results : 
                     Array.isArray(message.content) ? message.content : [];
      const tableData = transformToTableData(results);

      if (tableData.length === 0) {
        return (
          <p className="text-sm text-gray-600">
            No data available for table visualization.
          </p>
        );
      }

      return (
        <div>
          <ProductTable data={tableData} apiData={apiData} />
          {apiData.insights && renderInsights(apiData.insights)}
        </div>
      );
    } catch (error) {
      console.error('Error rendering table:', error);
      return (
        <div className="text-sm text-red-600">
          Error rendering table: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      );
    }
  }

  if (message.type === 'suggestions') {
    return (
      <Suggestions 
        suggestions={message.content} 
        onSuggestionClick={onSuggestionClick}
        isLoading={false}
      />
    );
  }

  if (message.type === 'followup-suggestions') {
    return (
      <FollowupSuggestions 
        suggestions={message.content} 
        onSuggestionClick={onSuggestionClick}
        disabled={isQueryLoading}
      />
    );
  }

  return null;
};

export default MessageContent;

